import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Button, Form, Table, Modal, Badge, Alert, Tab, Tabs, InputGroup, Spinner, Toast } from 'react-bootstrap';
import { collection, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import enhancedFacilitatorService, { FacilitatorServiceError, ErrorCodes } from '../services/enhancedFacilitatorService';
import facilitatorService from '../services/facilitatorService';
import programAssignmentService from '../services/programAssignmentService';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import ContentUpload from './ContentUpload';
import RichContentEditor from './RichContentEditor';
import { DIFFICULTY_OPTIONS, getDifficultyColor, migrateLegacyDifficulty } from '../config/difficultyLevels';
import googleMeetIntegrationService from '../services/googleMeetIntegrationService';

const ModuleBuilder = () => {
  const { currentUser, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('create-module');
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [modules, setModules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignedPrograms, setAssignedPrograms] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [moduleValidated, setModuleValidated] = useState(false);
  const [moduleModalError, setModuleModalError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  
  const [newModule, setNewModule] = useState({
    moduleName: '',
    description: '',
    startDate: '',
    endDate: '',
    difficulty: 'Incube Level 1',
    estimatedHours: 0,
    learningObjectives: [''],
    prerequisites: [],
    programId: '',
    cohortIds: [],
    visibility: 'program'
  });

  const [newLesson, setNewLesson] = useState({
    title: '',
    duration: '',
    type: 'video',
    content: '',
    richContent: '',
    liveSessionDate: '',
    liveSessionTime: '',
    facilitator: '',
    meetLink: '',
    notesReleaseDelay: 5,
    hasLiveSession: true,
    materials: [''],
    releaseControl: {
      releaseType: 'timer', // 'timer', 'manual', 'prerequisite'
      releaseDelay: 5, // minutes after session start
      prerequisiteModule: '',
      isReleased: false
    },
    contentElements: {
      hasVideo: false,
      hasDocuments: false,
      hasInteractiveContent: false,
      hasAssessment: false
    },
    templateCategory: 'custom' // 'custom', 'lecture', 'workshop', 'assessment'
  });
  
  const [showRichEditor, setShowRichEditor] = useState(false);
  const [generatingMeetLink, setGeneratingMeetLink] = useState(false);
  const [meetIntegrationEnabled, setMeetIntegrationEnabled] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [managingModule, setManagingModule] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [newAssignment, setNewAssignment] = useState({
    title: '',
    description: '',
    dueDate: '',
    dueTime: '23:59',
    priority: 'medium',
    estimatedHours: 1,
    maxScore: 100,
    type: 'assignment',
    submissionFormat: 'online',
    instructions: '',
    rubric: ['']
  });

  const [scheduleOverview, setScheduleOverview] = useState({
    startDate: '',
    endDate: '',
    sessionsPerWeek: 2,
    sessionDuration: 2,
    preferredDays: ['monday', 'wednesday'],
    preferredTime: '10:00'
  });

  const moduleRowData = useMemo(() => {
    return modules.map((module) => {
      const moduleAssignments = assignments.filter((assignment) =>
        assignment.moduleId === module.id || (module.moduleCode && assignment.moduleId === module.moduleCode)
      );
      const lessonCount = module.lessons ? module.lessons.length : 0;
      const liveSessionCount = (module.lessons || []).filter((lesson) => lesson?.hasLiveSession).length;
      const resourceCount = module.content?.length || 0;

      return {
        ...module,
        moduleAssignments,
        lessonCount,
        liveSessionCount,
        resourceCount
      };
    });
  }, [modules, assignments]);

  const moduleStats = useMemo(() => {
    return {
      modules: modules.length,
      lessons: modules.reduce((sum, module) => sum + (module.lessons ? module.lessons.length : 0), 0),
      assignments: assignments.length,
      resources: modules.reduce((sum, module) => sum + (module.content?.length || 0), 0)
    };
  }, [modules, assignments]);

  // Load modules and assignments on component mount
  useEffect(() => {
    loadData();
    initializeGoogleMeetIntegration();
  }, [currentUser]);

  const initializeGoogleMeetIntegration = async () => {
    try {
      const result = await googleMeetIntegrationService.initialize();
      setMeetIntegrationEnabled(result.success);
    } catch (error) {
      devLog('Google Meet integration not available:', error);
      setMeetIntegrationEnabled(false);
    }
  };

  const calculateEndTime = (startTime, durationMinutes) => {
    if (!startTime || !durationMinutes) return startTime;
    
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + parseInt(durationMinutes);
    const endHours = Math.floor(totalMinutes / 60);
    const endMins = totalMinutes % 60;
    
    return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
  };

  const loadData = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);

      // Load assigned programs first
      let programs = [];
      try {
        programs = await programAssignmentService.getFacilitatorPrograms();
        setAssignedPrograms(programs);
      } catch (error) {
        devLog('Could not load assigned programs:', error);
        setAssignedPrograms([]);
      }

      const [cohortResult, moduleResult, assignmentResult] = await Promise.allSettled([
        getDocs(collection(db, 'cohorts')),
        enhancedFacilitatorService.getModules(null, { pageSize: 100 }),
        getDocs(collection(db, 'assignments'))
      ]);

      if (cohortResult.status === 'fulfilled') {
        let cohortsData = cohortResult.value.docs.map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data()
        }));

        if (userProfile?.role !== 'admin') {
          const assignedCohorts = userProfile?.assignedCohorts || [];
          if (assignedCohorts.length > 0) {
            cohortsData = cohortsData.filter((cohort) => assignedCohorts.includes(cohort.id));
          } else {
            const programIds = programs.map((program) => program.id);
            if (programIds.length > 0) {
              cohortsData = cohortsData.filter((cohort) => programIds.includes(cohort.programId));
            }
          }
        }

        setCohorts(cohortsData);
      } else {
        devLog('Could not load cohorts:', cohortResult.reason);
        setCohorts([]);
      }

      if (moduleResult.status === 'fulfilled') {
        setModules(
          moduleResult.value.map((module) => ({
            ...module,
            difficulty: migrateLegacyDifficulty(module.difficulty || 'Incube Level 1')
          }))
        );
      } else {
        throw moduleResult.reason;
      }

      if (assignmentResult.status === 'fulfilled') {
        const assignmentsData = assignmentResult.value.docs.map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data()
        }));
        setAssignments(assignmentsData || []);
      } else {
        devLog('Could not load assignments:', assignmentResult.reason);
        setAssignments([]);
      }
    } catch (error) {
      devError('Error loading data:', error);
      showToastMessage('Error loading modules and assignments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToastMessage = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Helper functions
  const addArrayItem = (setter, field) => {
    setter(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateArrayItem = (setter, field, index, value) => {
    setter(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeArrayItem = (setter, field, index) => {
    setter(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const calculateNotesReleaseTime = (sessionDate, sessionTime, delayMinutes = 5) => {
    return enhancedFacilitatorService.calculateNotesReleaseTime(sessionDate, sessionTime, delayMinutes);
  };

  const generateMeetLink = () => {
    return enhancedFacilitatorService.generateMeetLink?.() || '';
  };

  const resetModuleForm = () => {
    setModuleModalError('');
    setNewModule({
      moduleName: '',
      description: '',
      startDate: '',
      endDate: '',
      difficulty: 'Incube Level 1',
      estimatedHours: 1,
      programId: '',
      learningObjectives: [''],
      prerequisites: [],
      cohortIds: [],
      visibility: 'program'
    });
    setEditingModuleId(null);
    setModuleValidated(false);
  };

  const handleOpenModuleModal = (module = null) => {
    setModuleModalError('');
    if (module) {
      const hasCohorts = Array.isArray(module.cohortIds) && module.cohortIds.length > 0;
      const startDateValue = module.startDate?.toDate ? module.startDate.toDate() : module.startDate;
      const endDateValue = module.endDate?.toDate ? module.endDate.toDate() : module.endDate;
      setNewModule({
        moduleName: module.moduleName || '',
        description: module.description || '',
        startDate: startDateValue ? new Date(startDateValue).toISOString().split('T')[0] : '',
        endDate: endDateValue ? new Date(endDateValue).toISOString().split('T')[0] : '',
        difficulty: module.difficulty || 'Incube Level 1',
        estimatedHours: module.estimatedHours || 0,
        programId: module.programId || '',
        learningObjectives: module.learningObjectives?.length ? module.learningObjectives : [''],
        prerequisites: module.prerequisites || [],
        cohortIds: hasCohorts ? module.cohortIds : [],
        visibility: hasCohorts ? 'cohort' : 'program'
      });
      setEditingModuleId(module.id);
    } else {
      resetModuleForm();
    }
    setShowModuleModal(true);
  };

  const handleCreateModule = async () => {
    setModuleValidated(true);
    setModuleModalError('');
    try {
      if (!newModule.moduleName || !newModule.description || !newModule.programId) {
        setModuleModalError('Please fill in all required fields including program selection.');
        return;
      }

      if (!newModule.startDate || !newModule.endDate) {
        setModuleModalError('Please provide a start and end date for the module.');
        return;
      }

      const parsedEstimatedHours = Number(newModule.estimatedHours);
      if (!Number.isFinite(parsedEstimatedHours) || parsedEstimatedHours < 1) {
        setModuleModalError('Estimated hours must be at least 1.');
        return;
      }

      const trimmedObjectives = (newModule.learningObjectives || [])
        .map((objective) => (objective || '').trim())
        .filter(Boolean);
      if (trimmedObjectives.length === 0) {
        setModuleModalError('Please add at least one learning objective.');
        return;
      }

      if (newModule.visibility === 'cohort' && newModule.cohortIds.length === 0) {
        setModuleModalError('Please select at least one cohort for cohort-only visibility.');
        return;
      }

      setSubmitting(true);
      const payload = {
        ...newModule,
        cohortIds: newModule.visibility === 'program' ? [] : newModule.cohortIds,
        estimatedHours: parsedEstimatedHours,
        learningObjectives: trimmedObjectives
      };

      if (editingModuleId) {
        await enhancedFacilitatorService.updateModule(editingModuleId, payload);
        showToastMessage('Module updated successfully!', 'success');
      } else {
        await enhancedFacilitatorService.createModule(payload);
        showToastMessage('Module created successfully!', 'success');
      }
      
      setShowModuleModal(false);
      setModuleModalError('');
      
      // Reset form
      resetModuleForm();
      
      // Reload data
      await loadData();
    } catch (error) {
      devError('Error creating module:', error);
      
      let errorMessage = 'Error creating module. Please try again.';
      if (error instanceof FacilitatorServiceError) {
        switch (error.code) {
          case ErrorCodes.VALIDATION_FAILED:
            errorMessage = `Validation failed: ${error.details?.map(d => d.message).join(', ') || error.message}`;
            break;
          case ErrorCodes.AUTH_REQUIRED:
            errorMessage = 'Please log in to continue.';
            break;
          case ErrorCodes.INSUFFICIENT_PERMISSIONS:
            errorMessage = 'You do not have permission to create modules.';
            break;
          default:
            errorMessage = error.message;
        }
      }
      
      setModuleModalError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateLesson = async () => {
    if (!selectedModule) return;

    try {
      if (!newLesson.title) {
        showToastMessage('Please enter a lesson title', 'error');
        return;
      }

      setSubmitting(true);
      const sessionDateTime = newLesson.hasLiveSession ?
        `${newLesson.liveSessionDate}T${newLesson.liveSessionTime}` : null;
      
      const notesReleaseTime = newLesson.hasLiveSession ? 
        calculateNotesReleaseTime(
          newLesson.liveSessionDate, 
          newLesson.liveSessionTime, 
          newLesson.notesReleaseDelay
        ) : null;

      let meetLink = '';
      let meetMeeting = null;

      // Generate Google Meet meeting if live session is enabled
      if (newLesson.hasLiveSession && meetIntegrationEnabled) {
        setGeneratingMeetLink(true);

        const sessionData = {
          title: newLesson.title,
          moduleName: selectedModule.moduleName,
          description: newLesson.content,
          date: newLesson.liveSessionDate,
          startTime: newLesson.liveSessionTime,
          endTime: calculateEndTime(newLesson.liveSessionTime, newLesson.duration),
          facilitatorName: currentUser?.displayName || 'TBD',
          objectives: selectedModule.learningObjectives || [],
          attendees: []
        };

        const meetResult = await googleMeetIntegrationService.createMeeting(sessionData);

        if (meetResult.success) {
          meetLink = meetResult.meeting.joinUrl || meetResult.meeting.hangoutLink || '';
          meetMeeting = meetResult.meeting;
          showToastMessage('Google Meet created and calendar event saved!', 'success');
        } else {
          devLog('Failed to create Google Meet:', meetResult.error);
          meetLink = newLesson.meetLink || generateMeetLink();
        }

        setGeneratingMeetLink(false);
      } else if (newLesson.hasLiveSession && newLesson.meetLink) {
        meetLink = newLesson.meetLink;
      }

      const lessonData = {
        ...newLesson,
        scheduledDate: sessionDateTime,
        notesReleaseTime: notesReleaseTime,
        meetLink: meetLink,
        meetMeetingId: meetMeeting?.id,
        meetMeetingData: meetMeeting,
        platform: 'google-meet',
        status: 'scheduled'
      };

      // For now, use the basic service for lessons until enhanced lesson creation is implemented
      await facilitatorService.addLessonToModule(selectedModule.id, lessonData);
      
      showToastMessage('Lesson added successfully!', 'success');
      setShowLessonModal(false);
      
      // Reset form
      setNewLesson({
        title: '',
        duration: '',
        type: 'video',
        content: '',
        liveSessionDate: '',
        liveSessionTime: '',
        facilitator: '',
        meetLink: '',
        notesReleaseDelay: 5,
        hasLiveSession: true,
        materials: ['']
      });
      
      // Reload data
      await loadData();
    } catch (error) {
      devError('Error creating lesson:', error);
      
      let errorMessage = 'Error creating lesson. Please try again.';
      if (error instanceof FacilitatorServiceError) {
        switch (error.code) {
          case ErrorCodes.VALIDATION_FAILED:
            errorMessage = `Validation failed: ${error.details?.map(d => d.message).join(', ') || error.message}`;
            break;
          default:
            errorMessage = error.message;
        }
      }
      
      showToastMessage(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAssignment = async () => {
    if (!selectedModule) return;

    try {
      if (!newAssignment.title || !newAssignment.dueDate) {
        showToastMessage('Please fill in title and due date', 'error');
        return;
      }

      setSubmitting(true);
      const dueDateTimeISO = `${newAssignment.dueDate}T${newAssignment.dueTime}:00`;
      
      const assignmentData = {
        moduleId: selectedModule.id,
        ...newAssignment,
        dueDate: dueDateTimeISO
      };

      await enhancedFacilitatorService.createAssignment(assignmentData);
      
      showToastMessage('Assignment created successfully!', 'success');
      setShowAssignmentModal(false);
      
      // Reset form
      setNewAssignment({
        title: '',
        description: '',
        dueDate: '',
        dueTime: '23:59',
        priority: 'medium',
        estimatedHours: 1,
        maxScore: 100,
        type: 'assignment',
        submissionFormat: 'online',
        instructions: '',
        rubric: ['']
      });
      
      // Load assignments for the module - implement getAssignmentsByModule in enhanced service later
      // const moduleAssignments = await enhancedFacilitatorService.getAssignmentsByModule(selectedModule.id);
      // setAssignments(prev => [...prev.filter(a => a.moduleId !== selectedModule.id), ...moduleAssignments]);
      
    } catch (error) {
      devError('Error creating assignment:', error);
      
      let errorMessage = 'Error creating assignment. Please try again.';
      if (error instanceof FacilitatorServiceError) {
        switch (error.code) {
          case ErrorCodes.VALIDATION_FAILED:
            errorMessage = `Validation failed: ${error.details?.map(d => d.message).join(', ') || error.message}`;
            break;
          case ErrorCodes.RESOURCE_NOT_FOUND:
            errorMessage = 'Module not found. Please select a valid module.';
            break;
          default:
            errorMessage = error.message;
        }
      }
      
      showToastMessage(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteModule = async (module) => {
    if (!window.confirm(`Delete module "${module.moduleName}"? This cannot be undone.`)) return;
    setDeletingId(module.id);
    try {
      await deleteDoc(doc(db, 'modules', module.id));
      setModules(prev => prev.filter(m => m.id !== module.id));
      setAssignments(prev => prev.filter(a => a.moduleId !== module.id));
      if (managingModule?.id === module.id) setShowManageModal(false);
      showToastMessage(`Module "${module.moduleName}" deleted.`, 'success');
    } catch (error) {
      devError('Error deleting module:', error);
      showToastMessage('Error deleting module. Please try again.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteLesson = async (module, lessonId) => {
    if (!window.confirm('Delete this lesson? This cannot be undone.')) return;
    setDeletingId(lessonId);
    try {
      const updatedLessons = (module.lessons || []).filter(l => l.id !== lessonId);
      await updateDoc(doc(db, 'modules', module.id), { lessons: updatedLessons });
      const updatedModule = { ...module, lessons: updatedLessons };
      setModules(prev => prev.map(m => m.id === module.id ? updatedModule : m));
      setManagingModule(updatedModule);
      showToastMessage('Lesson deleted.', 'success');
    } catch (error) {
      devError('Error deleting lesson:', error);
      showToastMessage('Error deleting lesson. Please try again.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAssignment = async (assignment) => {
    if (!window.confirm(`Delete assignment "${assignment.title}"? This cannot be undone.`)) return;
    setDeletingId(assignment.id);
    try {
      await deleteDoc(doc(db, 'assignments', assignment.id));
      setAssignments(prev => prev.filter(a => a.id !== assignment.id));
      showToastMessage(`Assignment "${assignment.title}" deleted.`, 'success');
    } catch (error) {
      devError('Error deleting assignment:', error);
      showToastMessage('Error deleting assignment. Please try again.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const generateModuleSchedule = () => {
    if (!scheduleOverview.startDate || !scheduleOverview.endDate) {
      alert('Please set start and end dates first');
      return;
    }

    const startDate = new Date(scheduleOverview.startDate);
    const endDate = new Date(scheduleOverview.endDate);
    const totalWeeks = Math.ceil((endDate - startDate) / (7 * 24 * 60 * 60 * 1000));
    const totalSessions = totalWeeks * scheduleOverview.sessionsPerWeek;

    devLog(`Generating schedule: ${totalSessions} sessions over ${totalWeeks} weeks`);
    // In real implementation, this would create a full schedule

    alert(`Schedule generated: ${totalSessions} sessions over ${totalWeeks} weeks`);
  };

  const handleUseTemplate = (template) => {
    // Pre-populate module form with template data
    setNewModule({
      moduleName: template.name,
      description: `${template.name} - A comprehensive ${template.category} program`,
      startDate: scheduleOverview.startDate || '',
      endDate: scheduleOverview.endDate || '',
      difficulty: 'Beginner',
      estimatedHours: parseInt(template.duration.split(' ')[0]) || 20,
      programId: '',
      learningObjectives: [
        `Complete ${template.lessons} structured lessons`,
        `Submit ${template.assignments} practical assignments`,
        `Demonstrate proficiency in ${template.category} skills`
      ],
      prerequisites: []
    });

    // Auto-configure content elements based on template
    setNewLesson(prev => ({
      ...prev,
      templateCategory: template.category,
      contentElements: {
        hasVideo: template.contentElements.includes('videos'),
        hasDocuments: template.contentElements.includes('documents'),
        hasInteractiveContent: template.contentElements.includes('interactive'),
        hasAssessment: template.contentElements.includes('assessments')
      },
      releaseControl: {
        releaseType: template.releasePattern === 'weekly' ? 'timer' :
                     template.releasePattern === 'sequential' ? 'prerequisite' : 'manual',
        releaseDelay: 5,
        prerequisiteModule: '',
        isReleased: false
      }
    }));

    setShowModuleModal(true);
    showToastMessage(`Template "${template.name}" loaded successfully!`, 'success');
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'success';
      case 'Intermediate': return 'warning';
      case 'Advanced': return 'danger';
      default: return 'secondary';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  if (loading && modules.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="module-builder">
      {/* Toast Notification */}
      <div className="position-fixed top-0 end-0 p-3 app-toast-layer">
        <Toast show={showToast} onClose={() => setShowToast(false)} bg={toastVariant === 'error' ? 'danger' : toastVariant}>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toastVariant === 'success' ? 'Success' : 'Error'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </div>
      <Card className="mb-4">
        <Card.Header>
          <h4 className="mb-0">Advanced Module Builder</h4>
          <small className="text-muted">Create comprehensive learning modules with live sessions, assignments, and automated note distribution</small>
        </Card.Header>
      </Card>

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
        <Tab eventKey="create-module" title="Create Module">
          <Row>
            <Col md={8}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Module Creation</h5>
                  <Button variant="primary" onClick={() => handleOpenModuleModal()}>
                    + Create New Module
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Module Name</th>
                        <th>Difficulty</th>
                        <th>Duration</th>
                        <th>Period</th>
                        <th>Lessons</th>
                        <th>Live Sessions</th>
                        <th>Assignments</th>
                        <th>Resources</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {moduleRowData.map(module => {
                        return (
                          <tr key={module.id}>
                            <td>
                              <strong>{module.moduleName}</strong>
                              <br />
                              <small className="text-muted">{module.description ? module.description.substring(0, 50) + '...' : 'No description'}</small>
                            </td>
                            <td>
                              <Badge bg={getDifficultyColor(module.difficulty)}>
                                {module.difficulty}
                              </Badge>
                            </td>
                            <td>{module.estimatedHours}h</td>
                            <td>
                              <small>
                                {module.startDate ? new Date(module.startDate).toLocaleDateString() : 'TBD'} - {module.endDate ? new Date(module.endDate).toLocaleDateString() : 'TBD'}
                              </small>
                            </td>
                            <td>{module.lessonCount}</td>
                            <td>
                              <div>{module.liveSessionCount}</div>
                              <small className="text-muted">session-linked</small>
                            </td>
                            <td>{module.moduleAssignments.length}</td>
                            <td>
                              <div>{module.resourceCount}</div>
                              <small className="text-muted">linked resources</small>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleOpenModuleModal(module)}
                                >
                                  ✏️ Edit Module
                                </Button>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => {
                                    setSelectedModule(module);
                                    setShowLessonModal(true);
                                  }}
                                >
                                  + Add Lesson
                                </Button>
                                <Button 
                                  variant="outline-success" 
                                  size="sm"
                                  onClick={() => {
                                    setSelectedModule(module);
                                    setShowAssignmentModal(true);
                                  }}
                                >
                                  + Add Assignment
                                </Button>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedModule(module);
                                    setActiveTab('content-management');
                                  }}
                                >
                                  📤 Upload Content
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => {
                                    setManagingModule(module);
                                    setShowManageModal(true);
                                  }}
                                >
                                  📋 Manage Items
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={deletingId === module.id}
                                  onClick={() => handleDeleteModule(module)}
                                >
                                  {deletingId === module.id ? (
                                    <><Spinner animation="border" size="sm" className="me-1" />Deleting...</>
                                  ) : '🗑️ Delete Module'}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Quick Actions</h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button variant="primary" onClick={() => setShowModuleModal(true)}>
                      Create New Module
                    </Button>
                    <Button variant="outline-primary" onClick={() => setActiveTab('schedule-overview')}>
                      📅 Schedule Overview
                    </Button>
                    <Button variant="outline-secondary" onClick={() => setActiveTab('templates')}>
                      📋 Use Template
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              <Card className="mt-3">
                <Card.Header>
                  <h6 className="mb-0">Statistics</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col className="text-center">
                      <h4 className="text-primary">{moduleStats.modules}</h4>
                      <small>Modules</small>
                    </Col>
                    <Col className="text-center">
                      <h4 className="text-success">{moduleStats.lessons}</h4>
                      <small>Lessons</small>
                    </Col>
                    <Col className="text-center">
                      <h4 className="text-warning">{moduleStats.assignments}</h4>
                      <small>Assignments</small>
                    </Col>
                    <Col className="text-center">
                      <h4 className="text-info">{moduleStats.resources}</h4>
                      <small>Resources</small>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="schedule-overview" title="Schedule Overview">
          <Card>
            <Card.Header>
              <h5 className="mb-0">📅 Module Schedule Planning</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Module Start Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={scheduleOverview.startDate}
                            onChange={(e) => setScheduleOverview(prev => ({...prev, startDate: e.target.value}))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Module End Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={scheduleOverview.endDate}
                            onChange={(e) => setScheduleOverview(prev => ({...prev, endDate: e.target.value}))}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Sessions per Week</Form.Label>
                          <Form.Select
                            value={scheduleOverview.sessionsPerWeek}
                            onChange={(e) => setScheduleOverview(prev => ({...prev, sessionsPerWeek: parseInt(e.target.value)}))}
                          >
                            <option value={1}>1 session</option>
                            <option value={2}>2 sessions</option>
                            <option value={3}>3 sessions</option>
                            <option value={4}>4 sessions</option>
                            <option value={5}>5 sessions</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Session Duration (hours)</Form.Label>
                          <Form.Select
                            value={scheduleOverview.sessionDuration}
                            onChange={(e) => setScheduleOverview(prev => ({...prev, sessionDuration: parseFloat(e.target.value)}))}
                          >
                            <option value={1}>1 hour</option>
                            <option value={1.5}>1.5 hours</option>
                            <option value={2}>2 hours</option>
                            <option value={2.5}>2.5 hours</option>
                            <option value={3}>3 hours</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label>Preferred Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={scheduleOverview.preferredTime}
                        onChange={(e) => setScheduleOverview(prev => ({...prev, preferredTime: e.target.value}))}
                      />
                    </Form.Group>
                    <Button variant="success" onClick={generateModuleSchedule}>
                      Generate Schedule
                    </Button>
                  </Form>
                </Col>
                <Col md={6}>
                  <Alert variant="info">
                    <h6>📋 Schedule Guidelines</h6>
                    <ul className="mb-0">
                      <li>Live session notes are automatically released 5 minutes after each session ends</li>
                      <li>Assignment due dates are typically set 1 week after related lessons</li>
                      <li>Google Meet links are auto-generated for all scheduled sessions</li>
                      <li>Students receive notifications 24h and 1h before each session</li>
                      <li>Session recordings are available 1 hour after session completion</li>
                    </ul>
                  </Alert>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="content-management" title="Content Management">
          <Row>
            <Col md={8}>
              {selectedModule ? (
                <ContentUpload 
                  moduleId={selectedModule.id} 
                  onUploadComplete={(content) => {
                    showToastMessage(`Content "${content.title}" uploaded successfully!`, 'success');
                    loadData(); // Refresh module data
                  }}
                />
              ) : (
                <Alert variant="info">
                  <h6>📤 Content Upload</h6>
                  <p>Select a module from the "Create Module" tab to upload videos, documents, and other learning materials.</p>
                  <Button variant="primary" onClick={() => setActiveTab('create-module')}>
                    Go to Modules
                  </Button>
                </Alert>
              )}
            </Col>
            <Col md={4}>
              <Card>
                <Card.Header>
                  <h6 className="mb-0">📁 Content Guidelines</h6>
                </Card.Header>
                <Card.Body>
                  <div className="content-guidelines">
                    <h6 className="text-primary">Video Content</h6>
                    <ul className="small mb-3">
                      <li>Max file size: 500MB</li>
                      <li>Recommended: MP4 format</li>
                      <li>Resolution: 720p or higher</li>
                      <li>Include captions when possible</li>
                    </ul>
                    
                    <h6 className="text-success">Documents</h6>
                    <ul className="small mb-3">
                      <li>Max file size: 50MB</li>
                      <li>PDF preferred for reading materials</li>
                      <li>Use descriptive filenames</li>
                      <li>Include version numbers if applicable</li>
                    </ul>
                    
                    <h6 className="text-warning">Best Practices</h6>
                    <ul className="small mb-0">
                      <li>Add detailed descriptions</li>
                      <li>Use relevant tags for searchability</li>
                      <li>Set appropriate access levels</li>
                      <li>Test content accessibility</li>
                    </ul>
                  </div>
                </Card.Body>
              </Card>
              
              {selectedModule && (
                <Card className="mt-3">
                  <Card.Header>
                    <h6 className="mb-0">Module Content Stats</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="content-stats">
                      <div className="stat-item mb-2">
                        <strong>Selected Module:</strong><br />
                        <span className="text-primary">{selectedModule.moduleName}</span>
                      </div>
                      <div className="stat-item mb-2">
                        <strong>Content Items:</strong><br />
                        <span className="text-success">{selectedModule.content?.length || 0}</span>
                      </div>
                      <div className="stat-item">
                        <strong>Total Size:</strong><br />
                        <span className="text-info">
                          {selectedModule.content?.reduce((total, item) => total + (item.file?.size || 0), 0) / (1024 * 1024) || 0} MB
                        </span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="templates" title="Module Templates">
          <Card>
            <Card.Header>
              <h5 className="mb-0">📋 Pre-built Module Templates</h5>
              <small className="text-muted">Use these templates to quickly create structured modules with predefined content elements</small>
            </Card.Header>
            <Card.Body>
              <Row>
                {[
                  {
                    name: 'Digital Literacy Basics',
                    lessons: 5,
                    assignments: 2,
                    duration: '35 hours',
                    category: 'digital',
                    contentElements: ['videos', 'documents', 'interactive'],
                    releasePattern: 'weekly'
                  },
                  {
                    name: 'Professional Skills',
                    lessons: 5,
                    assignments: 3,
                    duration: '32 hours',
                    category: 'business',
                    contentElements: ['workshops', 'assessments', 'portfolios'],
                    releasePattern: 'sequential'
                  },
                  {
                    name: 'Industry Certification Prep',
                    lessons: 5,
                    assignments: 4,
                    duration: '28 hours',
                    category: 'technical',
                    contentElements: ['practice-tests', 'study-guides', 'mock-exams'],
                    releasePattern: 'milestone-based'
                  },
                  {
                    name: 'Soft Skills Workshop',
                    lessons: 3,
                    assignments: 1,
                    duration: '15 hours',
                    category: 'leadership',
                    contentElements: ['group-activities', 'reflections', 'peer-feedback'],
                    releasePattern: 'session-based'
                  }
                ].map((template, idx) => (
                  <Col md={6} lg={3} key={idx} className="mb-3">
                    <Card className="border-primary h-100">
                      <Card.Body className="d-flex flex-column">
                        <h6>{template.name}</h6>
                        <p className="text-muted small flex-grow-1">
                          {template.lessons} lessons • {template.assignments} assignments<br />
                          {template.duration}
                        </p>
                        <div className="mb-2">
                          <Badge bg="info" className="me-1">{template.category}</Badge>
                          <Badge bg="secondary">{template.releasePattern}</Badge>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">Content Elements:</small>
                          <div>
                            {template.contentElements.map((element, i) => (
                              <Badge key={i} bg="outline-success" className="me-1 mb-1" style={{fontSize: '0.7em'}}>
                                {element}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="w-100 mt-auto"
                          onClick={() => handleUseTemplate(template)}
                        >
                          Use Template
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              <hr className="my-4" />

              <Card className="border-warning">
                <Card.Header className="bg-warning bg-opacity-10">
                  <h6 className="mb-0">Template Features</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <h6 className="text-primary">📚 Content Elements</h6>
                      <ul className="small mb-3">
                        <li><strong>Title & Duration:</strong> Auto-generated based on template</li>
                        <li><strong>Meet Link:</strong> Auto-created for live sessions</li>
                        <li><strong>Content Upload:</strong> Structured folders for each element</li>
                        <li><strong>Materials List:</strong> Pre-populated with common resources</li>
                      </ul>
                    </Col>
                    <Col md={6}>
                      <h6 className="text-success">⏱️ Release Control</h6>
                      <ul className="small mb-3">
                        <li><strong>Timer System:</strong> Auto-release after session start</li>
                        <li><strong>Sequential:</strong> Release after previous completion</li>
                        <li><strong>Milestone-based:</strong> Release after assessment completion</li>
                        <li><strong>Manual Override:</strong> Facilitator can release early</li>
                      </ul>
                    </Col>
                  </Row>
                  <Alert variant="info" className="mt-3">
                    <small>
                      <strong>💡 Pro Tip:</strong> Templates automatically configure content release schedules and notification triggers.
                      You can customize any template after creation to match your specific needs.
                    </small>
                  </Alert>
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Create Module Modal */}
      <Modal show={showModuleModal} onHide={() => { setShowModuleModal(false); setModuleModalError(''); resetModuleForm(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingModuleId ? 'Edit Module' : 'Create New Module'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {moduleModalError && (
            <Alert variant="danger" className="modal-error-alert" dismissible onClose={() => setModuleModalError('')}>
              <Alert.Heading>Module Could Not Be Saved</Alert.Heading>
              <div>{moduleModalError}</div>
            </Alert>
          )}
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Module Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newModule.moduleName}
                    onChange={(e) => setNewModule(prev => ({...prev, moduleName: e.target.value}))}
                    placeholder="Enter module name"
                    required
                    isInvalid={moduleValidated && !newModule.moduleName}
                  />
                  <Form.Control.Feedback type="invalid">Module name is required.</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Assigned Program *</Form.Label>
                  <Form.Select
                    value={newModule.programId}
                    onChange={(e) => setNewModule(prev => ({...prev, programId: e.target.value}))}
                    required
                    isInvalid={moduleValidated && !newModule.programId}
                  >
                    <option value="">Select a program</option>
                    {assignedPrograms.map(program => (
                      <option key={program.id} value={program.id}>
                        {program.name} ({program.category})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">Please select a programme.</Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    {assignedPrograms.length === 0
                      ? 'No programmes assigned. Contact administrator.'
                      : 'Module will be created under this programme'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Visibility</Form.Label>
              <div className="mb-2">
                <Form.Check
                  type="radio"
                  id="module-visibility-program"
                  name="module-visibility"
                  label="Program-wide (all cohorts in this program)"
                  checked={newModule.visibility === 'program'}
                  onChange={() => setNewModule(prev => ({
                    ...prev,
                    visibility: 'program',
                    cohortIds: []
                  }))}
                />
                <Form.Check
                  type="radio"
                  id="module-visibility-cohort"
                  name="module-visibility"
                  label="Selected cohorts only"
                  checked={newModule.visibility === 'cohort'}
                  onChange={() => setNewModule(prev => ({
                    ...prev,
                    visibility: 'cohort'
                  }))}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Assign to Cohorts (optional)</Form.Label>
              <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {cohorts
                  .filter((cohort) => !newModule.programId || cohort.programId === newModule.programId)
                  .map((cohort) => (
                    <Form.Check
                      key={cohort.id}
                      type="checkbox"
                      id={`module-cohort-${cohort.id}`}
                      label={cohort.name || cohort.cohortName || cohort.id}
                      checked={newModule.cohortIds.includes(cohort.id)}
                      disabled={newModule.visibility !== 'cohort'}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setNewModule((prev) => ({
                          ...prev,
                          cohortIds: checked
                            ? [...prev.cohortIds, cohort.id]
                            : prev.cohortIds.filter((id) => id !== cohort.id)
                        }));
                      }}
                    />
                  ))}
                {cohorts.filter((cohort) => !newModule.programId || cohort.programId === newModule.programId).length === 0 && (
                  <div className="text-muted small">No cohorts available for the selected program.</div>
                )}
              </div>
              <Form.Text className="text-muted">
                {newModule.visibility === 'cohort'
                  ? 'Only learners in selected cohorts will see this module.'
                  : 'All learners in the selected program will see this module.'}
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Difficulty Level</Form.Label>
                  <Form.Select
                    value={newModule.difficulty}
                    onChange={(e) => setNewModule(prev => ({...prev, difficulty: e.target.value}))}
                  >
                    {DIFFICULTY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {DIFFICULTY_OPTIONS.find(opt => opt.value === newModule.difficulty)?.description}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newModule.description}
                onChange={(e) => setNewModule(prev => ({...prev, description: e.target.value}))}
                placeholder="Describe what learners will cover in this module"
                required
                isInvalid={moduleValidated && !newModule.description}
              />
              <Form.Control.Feedback type="invalid">Description is required.</Form.Control.Feedback>
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={newModule.startDate}
                    onChange={(e) => setNewModule(prev => ({...prev, startDate: e.target.value}))}
                    required
                    isInvalid={moduleValidated && !newModule.startDate}
                  />
                  <Form.Control.Feedback type="invalid">Start date is required.</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={newModule.endDate}
                    onChange={(e) => setNewModule(prev => ({...prev, endDate: e.target.value}))}
                    required
                    isInvalid={moduleValidated && !newModule.endDate}
                  />
                  <Form.Control.Feedback type="invalid">End date is required.</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Estimated Hours</Form.Label>
                  <Form.Control
                    type="number"
                    value={newModule.estimatedHours}
                    onChange={(e) => setNewModule(prev => ({...prev, estimatedHours: parseInt(e.target.value)}))}
                    min="1"
                    max="200"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Learning Objectives</Form.Label>
              {newModule.learningObjectives.map((objective, idx) => (
                <div key={idx} className="d-flex mb-2">
                  <Form.Control
                    type="text"
                    value={objective}
                    onChange={(e) => updateArrayItem(setNewModule, 'learningObjectives', idx, e.target.value)}
                    placeholder="What will students achieve?"
                  />
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    className="ms-2"
                    onClick={() => removeArrayItem(setNewModule, 'learningObjectives', idx)}
                  >
                    ×
                  </Button>
                </div>
              ))}
              <Button 
                variant="outline-success" 
                size="sm"
                onClick={() => addArrayItem(setNewModule, 'learningObjectives')}
              >
                + Add Objective
              </Button>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModuleModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateModule} disabled={submitting}>
            {submitting ? (
              <><Spinner animation="border" size="sm" className="me-2" />{editingModuleId ? 'Saving...' : 'Creating...'}</>
            ) : (
              editingModuleId ? 'Save Changes' : 'Create Module'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Lesson Modal */}
      <Modal show={showLessonModal} onHide={() => setShowLessonModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>🎓 Add Lesson to {selectedModule?.moduleName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Lesson Title *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newLesson.title}
                    onChange={(e) => setNewLesson(prev => ({...prev, title: e.target.value}))}
                    placeholder="Enter lesson title"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Lesson Type</Form.Label>
                  <Form.Select
                    value={newLesson.type}
                    onChange={(e) => setNewLesson(prev => ({...prev, type: e.target.value}))}
                  >
                    <option value="video">Video Lesson</option>
                    <option value="workshop">Live Workshop</option>
                    <option value="project">Project Work</option>
                    <option value="assessment">Assessment</option>
                    <option value="discussion">Discussion</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration</Form.Label>
                  <Form.Control
                    type="text"
                    value={newLesson.duration}
                    onChange={(e) => setNewLesson(prev => ({...prev, duration: e.target.value}))}
                    placeholder="e.g., 2 hours"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Lesson Content Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newLesson.content}
                onChange={(e) => setNewLesson(prev => ({...prev, content: e.target.value}))}
                placeholder="Describe what will be covered in this lesson"
              />
            </Form.Group>

            <Form.Check
              type="checkbox"
              label="This lesson has a live session"
              checked={newLesson.hasLiveSession}
              onChange={(e) => setNewLesson(prev => ({...prev, hasLiveSession: e.target.checked}))}
              className="mb-3"
            />

            {newLesson.hasLiveSession && (
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">🔴 Live Session Details</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Session Date *</Form.Label>
                        <Form.Control
                          type="date"
                          value={newLesson.liveSessionDate}
                          onChange={(e) => setNewLesson(prev => ({...prev, liveSessionDate: e.target.value}))}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Session Time *</Form.Label>
                        <Form.Control
                          type="time"
                          value={newLesson.liveSessionTime}
                          onChange={(e) => setNewLesson(prev => ({...prev, liveSessionTime: e.target.value}))}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Facilitator</Form.Label>
                        <Form.Control
                          type="text"
                          value={newLesson.facilitator}
                          onChange={(e) => setNewLesson(prev => ({...prev, facilitator: e.target.value}))}
                          placeholder="Facilitator name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <Form.Label className="mb-0">Google Meet Integration</Form.Label>
                          {meetIntegrationEnabled ? (
                            <Badge bg="success" className="ms-2">✅ Connected</Badge>
                          ) : (
                            <Badge bg="warning" className="ms-2">⚠️ Manual</Badge>
                          )}
                        </div>
                        <InputGroup>
                          <Form.Control
                            type="url"
                            value={newLesson.meetLink}
                            onChange={(e) => setNewLesson(prev => ({...prev, meetLink: e.target.value}))}
                            placeholder={meetIntegrationEnabled ?
                              "Google Meet will be auto-created with calendar event" :
                              "Paste Google Meet link (e.g. https://meet.google.com/abc-defg-hij)"
                            }
                          />
                          {!meetIntegrationEnabled && (
                            <Button
                              variant="outline-secondary"
                              onClick={() => setNewLesson(prev => ({...prev, meetLink: generateMeetLink()}))}
                            >
                              🔗 Generate
                            </Button>
                          )}
                        </InputGroup>
                        {meetIntegrationEnabled ? (
                          <Form.Text className="text-success">
                            📅 Google Meet and calendar event will be automatically created when you save this lesson
                          </Form.Text>
                        ) : (
                          <Form.Text className="text-muted">
                            Connect your Google account in the dashboard to auto-create Meet links.
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Notes Release Delay (minutes)</Form.Label>
                        <Form.Control
                          type="number"
                          value={newLesson.notesReleaseDelay}
                          onChange={(e) => setNewLesson(prev => ({...prev, notesReleaseDelay: parseInt(e.target.value)}))}
                          min="0"
                          max="60"
                        />
                        <Form.Text className="text-muted">
                          Notes will be available {newLesson.notesReleaseDelay} minutes after session starts
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  {newLesson.liveSessionDate && newLesson.liveSessionTime && (
                    <Alert variant="info">
                      <strong>📅 Session Schedule:</strong> {new Date(`${newLesson.liveSessionDate}T${newLesson.liveSessionTime}`).toLocaleDateString()} at {new Date(`${newLesson.liveSessionDate}T${newLesson.liveSessionTime}`).toLocaleTimeString()}
                      <br />
                      <strong>📝 Notes Available:</strong> {new Date(calculateNotesReleaseTime(newLesson.liveSessionDate, newLesson.liveSessionTime, newLesson.notesReleaseDelay)).toLocaleTimeString()}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Content Elements Configuration */}
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0">📚 Content Elements</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Check
                      type="checkbox"
                      label="📹 Video Content"
                      checked={newLesson.contentElements.hasVideo}
                      onChange={(e) => setNewLesson(prev => ({
                        ...prev,
                        contentElements: { ...prev.contentElements, hasVideo: e.target.checked }
                      }))}
                      className="mb-2"
                    />
                    <Form.Check
                      type="checkbox"
                      label="📄 Documents/PDFs"
                      checked={newLesson.contentElements.hasDocuments}
                      onChange={(e) => setNewLesson(prev => ({
                        ...prev,
                        contentElements: { ...prev.contentElements, hasDocuments: e.target.checked }
                      }))}
                      className="mb-2"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="checkbox"
                      label="🎮 Interactive Content"
                      checked={newLesson.contentElements.hasInteractiveContent}
                      onChange={(e) => setNewLesson(prev => ({
                        ...prev,
                        contentElements: { ...prev.contentElements, hasInteractiveContent: e.target.checked }
                      }))}
                      className="mb-2"
                    />
                    <Form.Check
                      type="checkbox"
                      label="📝 Assessment/Quiz"
                      checked={newLesson.contentElements.hasAssessment}
                      onChange={(e) => setNewLesson(prev => ({
                        ...prev,
                        contentElements: { ...prev.contentElements, hasAssessment: e.target.checked }
                      }))}
                      className="mb-2"
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Release Control Configuration */}
            <Card className="mb-3">
              <Card.Header>
                <h6 className="mb-0">⏱️ Content Release Control</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Release Type</Form.Label>
                      <Form.Select
                        value={newLesson.releaseControl.releaseType}
                        onChange={(e) => setNewLesson(prev => ({
                          ...prev,
                          releaseControl: { ...prev.releaseControl, releaseType: e.target.value }
                        }))}
                      >
                        <option value="timer">Timer-based (auto-release after session)</option>
                        <option value="manual">Manual Release</option>
                        <option value="prerequisite">Prerequisite-based</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    {newLesson.releaseControl.releaseType === 'timer' && (
                      <Form.Group className="mb-3">
                        <Form.Label>Release Delay (minutes)</Form.Label>
                        <Form.Control
                          type="number"
                          value={newLesson.releaseControl.releaseDelay}
                          onChange={(e) => setNewLesson(prev => ({
                            ...prev,
                            releaseControl: { ...prev.releaseControl, releaseDelay: parseInt(e.target.value) }
                          }))}
                          min="0"
                          max="1440"
                        />
                        <Form.Text className="text-muted">
                          Content will be available {newLesson.releaseControl.releaseDelay} minutes after session starts
                        </Form.Text>
                      </Form.Group>
                    )}
                    {newLesson.releaseControl.releaseType === 'prerequisite' && (
                      <Form.Group className="mb-3">
                        <Form.Label>Prerequisite Module</Form.Label>
                        <Form.Select
                          value={newLesson.releaseControl.prerequisiteModule}
                          onChange={(e) => setNewLesson(prev => ({
                            ...prev,
                            releaseControl: { ...prev.releaseControl, prerequisiteModule: e.target.value }
                          }))}
                        >
                          <option value="">Select prerequisite module</option>
                          {modules.map(module => (
                            <option key={module.id} value={module.id}>
                              {module.moduleName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}
                  </Col>
                </Row>
                <Alert variant="warning" className="mt-2">
                  <small>
                    <strong>🚀 Release Control:</strong>
                    {newLesson.releaseControl.releaseType === 'timer' && ` Content automatically releases ${newLesson.releaseControl.releaseDelay} minutes after session start.`}
                    {newLesson.releaseControl.releaseType === 'manual' && ' You will need to manually release content when ready.'}
                    {newLesson.releaseControl.releaseType === 'prerequisite' && ' Content releases when prerequisite module is completed.'}
                    <br />
                    Students will receive notifications when content becomes available.
                  </small>
                </Alert>
                </Card.Body>
              </Card>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Learning Materials</Form.Label>
              {newLesson.materials.map((material, idx) => (
                <div key={idx} className="d-flex mb-2">
                  <Form.Control
                    type="text"
                    value={material}
                    onChange={(e) => updateArrayItem(setNewLesson, 'materials', idx, e.target.value)}
                    placeholder="Material name or URL"
                  />
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    className="ms-2"
                    onClick={() => removeArrayItem(setNewLesson, 'materials', idx)}
                  >
                    ×
                  </Button>
                </div>
              ))}
              <Button 
                variant="outline-success" 
                size="sm"
                onClick={() => addArrayItem(setNewLesson, 'materials')}
              >
                + Add Material
              </Button>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLessonModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateLesson}
            disabled={submitting || generatingMeetLink}
          >
            {generatingMeetLink ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating Google Meet...
              </>
            ) : submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating Lesson...
              </>
            ) : (
              'Create Lesson'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Assignment Modal */}
      <Modal show={showAssignmentModal} onHide={() => setShowAssignmentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Assignment to {selectedModule?.moduleName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Assignment Title *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newAssignment.title}
                    onChange={(e) => setNewAssignment(prev => ({...prev, title: e.target.value}))}
                    placeholder="Enter assignment title"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={newAssignment.priority}
                    onChange={(e) => setNewAssignment(prev => ({...prev, priority: e.target.value}))}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newAssignment.description}
                onChange={(e) => setNewAssignment(prev => ({...prev, description: e.target.value}))}
                placeholder="Describe the assignment requirements"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={newAssignment.dueDate}
                    onChange={(e) => setNewAssignment(prev => ({...prev, dueDate: e.target.value}))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={newAssignment.dueTime}
                    onChange={(e) => setNewAssignment(prev => ({...prev, dueTime: e.target.value}))}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Estimated Hours</Form.Label>
                  <Form.Control
                    type="number"
                    value={newAssignment.estimatedHours}
                    onChange={(e) => setNewAssignment(prev => ({...prev, estimatedHours: parseInt(e.target.value)}))}
                    min="1"
                    max="40"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Assignment Type</Form.Label>
                  <Form.Select
                    value={newAssignment.type}
                    onChange={(e) => setNewAssignment(prev => ({...prev, type: e.target.value}))}
                  >
                    <option value="assignment">Assignment</option>
                    <option value="project">Project</option>
                    <option value="assessment">Assessment</option>
                    <option value="portfolio">Portfolio</option>
                    <option value="presentation">Presentation</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Submission Format</Form.Label>
                  <Form.Select
                    value={newAssignment.submissionFormat}
                    onChange={(e) => setNewAssignment(prev => ({...prev, submissionFormat: e.target.value}))}
                  >
                    <option value="online">Online Submission</option>
                    <option value="document">Document Upload</option>
                    <option value="presentation">Presentation</option>
                    <option value="portfolio">Portfolio</option>
                    <option value="video">Video Recording</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Maximum Score</Form.Label>
                  <Form.Control
                    type="number"
                    value={newAssignment.maxScore}
                    onChange={(e) => setNewAssignment(prev => ({...prev, maxScore: parseInt(e.target.value)}))}
                    min="1"
                    max="200"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Detailed Instructions</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={newAssignment.instructions}
                onChange={(e) => setNewAssignment(prev => ({...prev, instructions: e.target.value}))}
                placeholder="Provide step-by-step instructions for completing this assignment"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Grading Rubric</Form.Label>
              {newAssignment.rubric.map((criteria, idx) => (
                <div key={idx} className="d-flex mb-2">
                  <Form.Control
                    type="text"
                    value={criteria}
                    onChange={(e) => updateArrayItem(setNewAssignment, 'rubric', idx, e.target.value)}
                    placeholder="Grading criteria"
                  />
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    className="ms-2"
                    onClick={() => removeArrayItem(setNewAssignment, 'rubric', idx)}
                  >
                    ×
                  </Button>
                </div>
              ))}
              <Button 
                variant="outline-success" 
                size="sm"
                onClick={() => addArrayItem(setNewAssignment, 'rubric')}
              >
                + Add Criteria
              </Button>
            </Form.Group>

            {newAssignment.dueDate && newAssignment.dueTime && (
              <Alert variant="warning">
                <strong>⏰ Assignment Due:</strong> {new Date(`${newAssignment.dueDate}T${newAssignment.dueTime}`).toLocaleString()}
                <br />
                <Badge bg={getPriorityColor(newAssignment.priority)} className="me-2">
                  {newAssignment.priority.toUpperCase()} Priority
                </Badge>
                Students will see this in their countdown timer.
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignmentModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateAssignment} disabled={submitting}>
            {submitting ? (
              <><Spinner animation="border" size="sm" className="me-2" />Creating...</>
            ) : 'Create Assignment'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Manage Items Modal */}
      <Modal show={showManageModal} onHide={() => setShowManageModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Manage: {managingModule?.moduleName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {managingModule && (
            <>
              <h6 className="mb-3">Lessons</h6>
              {(managingModule.lessons || []).length === 0 ? (
                <p className="text-muted small">No lessons yet.</p>
              ) : (
                <Table bordered size="sm" className="mb-4">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Type</th>
                      <th style={{ width: 110 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(managingModule.lessons || []).map(lesson => (
                      <tr key={lesson.id}>
                        <td>{lesson.title}</td>
                        <td><Badge bg="secondary">{lesson.type || 'lesson'}</Badge></td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={deletingId === lesson.id}
                            onClick={() => handleDeleteLesson(managingModule, lesson.id)}
                          >
                            {deletingId === lesson.id ? <Spinner animation="border" size="sm" /> : 'Delete'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <h6 className="mb-3">Assignments</h6>
              {assignments.filter(a => a.moduleId === managingModule.id).length === 0 ? (
                <p className="text-muted small">No assignments yet.</p>
              ) : (
                <Table bordered size="sm">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Due Date</th>
                      <th>Priority</th>
                      <th style={{ width: 110 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.filter(a => a.moduleId === managingModule.id).map(assignment => (
                      <tr key={assignment.id}>
                        <td>{assignment.title}</td>
                        <td><small>{assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : '—'}</small></td>
                        <td><Badge bg={getPriorityColor(assignment.priority)}>{assignment.priority || 'medium'}</Badge></td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            disabled={deletingId === assignment.id}
                            onClick={() => handleDeleteAssignment(assignment)}
                          >
                            {deletingId === assignment.id ? <Spinner animation="border" size="sm" /> : 'Delete'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowManageModal(false)}>
            Close
          </Button>
          <Button
            variant="danger"
            disabled={deletingId === managingModule?.id}
            onClick={() => managingModule && handleDeleteModule(managingModule)}
          >
            {deletingId === managingModule?.id ? (
              <><Spinner animation="border" size="sm" className="me-2" />Deleting...</>
            ) : '🗑️ Delete Entire Module'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ModuleBuilder;
